import React, { useEffect, useState } from 'react'
import { ShieldCheck } from 'lucide-react'
import { useCookieConsent } from '../hooks/useCookieConsent'
import './CookieConsent.css'

export default function CookieConsent({ onConsentUpdated }) {
  const {
    showBanner,
    preferences,
    acceptAll,
    rejectAll,
    saveCustomConsent,
  } = useCookieConsent({ onConsentUpdated })

  const [isConfigMode, setIsConfigMode] = useState(false)
  const [draftPreferences, setDraftPreferences] = useState(preferences)

  useEffect(() => {
    if (!isConfigMode) {
      setDraftPreferences(preferences)
    }
  }, [preferences, isConfigMode])

  if (!showBanner) {
    return null
  }

  const handleOpenConfig = () => {
    setDraftPreferences(preferences)
    setIsConfigMode(true)
  }

  const handleCancelConfig = () => {
    setDraftPreferences(preferences)
    setIsConfigMode(false)
  }

  const handlePrimaryAction = () => {
    if (isConfigMode) {
      saveCustomConsent(draftPreferences)
      setIsConfigMode(false)
      return
    }
    acceptAll()
  }

  const handleReject = () => {
    rejectAll()
    setIsConfigMode(false)
  }

  const handleSecondaryAction = () => {
    if (isConfigMode) {
      handleCancelConfig()
      return
    }
    handleReject()
  }

  const primaryLabel = isConfigMode ? 'Guardar selección' : 'Aceptar todo'
  const secondaryLabel = isConfigMode ? 'Cancelar' : 'Rechazar'

  const handleTogglePreference = key => {
    if (key === 'essential') return
    setDraftPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }))
  }

  return (
    <section
      className={`cookie-consent-card${isConfigMode ? ' cookie-consent-card--expanded' : ''}`}
      role="dialog"
      aria-live="polite"
      aria-label="Aviso de cookies"
    >
      <div className="cookie-consent-card__grid">
        <div className="cookie-consent-card__icon" aria-hidden="true">
          <ShieldCheck size={24} />
        </div>
        <div className="cookie-consent-card__text">
          <p className="cookie-consent-card__title">Tu privacidad nos importa</p>
          <p className="cookie-consent-card__body">
            Usamos cookies para mejorar tu experiencia y analizar el uso de la plataforma.
          </p>
          <div className="cookie-consent-card__links">
            <a className="cookie-consent-card__link" href="/cookies">
              Leer Política de Cookies
            </a>
            {!isConfigMode ? (
              <button
                type="button"
                className="cookie-consent-card__link cookie-consent-card__link--button"
                onClick={handleOpenConfig}
              >
                Configurar preferencias
              </button>
            ) : null}
          </div>
        </div>
      </div>

      {isConfigMode ? (
        <div className="cookie-consent-card__preferences">
          <PreferenceToggle
            label="Analíticas"
            description="Ayúdanos a entender el uso de la plataforma."
            checked={Boolean(draftPreferences.analytics)}
            onChange={() => handleTogglePreference('analytics')}
          />
          <PreferenceToggle
            label="Marketing"
            description="Publicidad personalizada y campañas."
            checked={Boolean(draftPreferences.marketing)}
            onChange={() => handleTogglePreference('marketing')}
          />
          <PreferenceToggle
            label="Esenciales"
            description="Obligatorias para que el sitio funcione."
            checked
            disabled
          />
        </div>
      ) : null}

      <div className="cookie-consent-card__actions">
        <button
          type="button"
          className="cookie-consent-card__button cookie-consent-card__button--primary"
          onClick={handlePrimaryAction}
        >
          {primaryLabel}
        </button>
        <button
          type="button"
          className="cookie-consent-card__button cookie-consent-card__button--ghost"
          onClick={handleSecondaryAction}
        >
          {secondaryLabel}
        </button>
      </div>
    </section>
  )
}

function PreferenceToggle({ label, description, checked, onChange, disabled }) {
  return (
    <label className={`cookie-preference${disabled ? ' cookie-preference--disabled' : ''}`}>
      <div>
        <span className="cookie-preference__label">{label}</span>
        <span className="cookie-preference__description">{description}</span>
      </div>
      <div className="cookie-switch">
        <input
          type="checkbox"
          checked={checked}
          onChange={disabled ? undefined : onChange}
          disabled={disabled}
        />
        <span className="cookie-switch__slider" />
      </div>
    </label>
  )
}
